import { XMarkIcon } from '@heroicons/react/24/outline';
import { PeriodPicker } from './PeriodPicker';
import { MultiSelectFilter } from './MultiSelectFilter';
import type { MultiSelectOption } from './MultiSelectFilter';

export type FilterField =
	| {
			type: 'multi';
			key: string;
			label: string;
			options: MultiSelectOption[];
			values: string[];
			onChange: (values: string[]) => void;
	  }
	| {
			type: 'period';
			key: string;
			from: string | null;
			to: string | null;
			onChange: (from: string | null, to: string | null) => void;
			today: Date;
			allowEmpty?: boolean;
			minDate?: string;
	  };

interface OverviewFiltersProps {
	fields: FilterField[];
	onClear: () => void;
	subtitle?: string;
}

function chipText(field: FilterField): string | null {
	if (field.type === 'multi') {
		return field.values.length > 0 ? `${field.label}: ${field.values.length}` : null;
	}
	if (field.type === 'period' && field.from && field.to) {
		return `Período: ${field.from.split('-').reverse().join('/')} a ${field.to.split('-').reverse().join('/')}`;
	}
	return null;
}

function clearField(field: FilterField) {
	if (field.type === 'multi') field.onChange([]);
	else if (field.type === 'period') field.onChange(null, null);
}

export function OverviewFilters({ fields, onClear, subtitle }: OverviewFiltersProps) {
	const chips = fields.map(field => ({ field, text: chipText(field) })).filter(c => c.text);

	return (
		<div className="space-y-3">
			<div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-tour="overview-filters">
				{fields.map(field => (
					field.type === 'multi' ? (
						<div key={field.key}>
							<label className="mb-1 block text-xs font-medium text-raro-gray-600">{field.label}</label>
							<MultiSelectFilter
								label={field.label}
								options={field.options}
								values={field.values}
								onChange={field.onChange}
							/>
						</div>
					) : (
						<div key={field.key} className="contents">
							<PeriodPicker
								from={field.from}
								to={field.to}
								onChange={field.onChange}
								today={field.today}
								allowEmpty={field.allowEmpty}
								minDate={field.minDate}
							/>
						</div>
					)
				))}
			</div>

			{subtitle && <p className="text-xs text-raro-gray-600">{subtitle}</p>}

			{chips.length > 0 && (
				<div className="flex flex-wrap items-center gap-2">
					{chips.map(({ field, text }) => (
						<span key={field.key} className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1">
							<span className="text-xs font-medium text-primary-700">{text}</span>
							<button
								type="button"
								onClick={() => clearField(field)}
								className="text-primary-600 hover:text-primary-800"
								aria-label={`Remover ${field.key}`}
							>
								<XMarkIcon className="h-3.5 w-3.5" />
							</button>
						</span>
					))}
					<button
						type="button"
						onClick={onClear}
						className="text-xs font-medium text-raro-gray-600 hover:text-raro-gray-900"
					>
						Limpar filtros
					</button>
				</div>
			)}
		</div>
	);
}
